import { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { config } from '../../config';
import { useAdminHeaders } from '../../auth/useAdminHeaders';

interface Component {
  id: string;
  name: string;
  display_name: string;
  status: string;
  region?: string;
  environment?: string;
}

export default function ComponentsAdmin() {
  const [components, setComponents] = useState<Component[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    status: 'OPERATIONAL',
    region: '',
    environment: 'production',
  });

  const apiUrl = config.apiUrl;
  const internalApiUrl = config.internalApiUrl;
  const getHeaders = useAdminHeaders();

  useEffect(() => {
    fetchComponents();
  }, []);

  async function fetchComponents() {
    try {
      const response = await fetch(`${apiUrl}/v1/stats/components`);
      if (response.ok) {
        const data = await response.json();
        setComponents(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch components', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    try {
      const response = await fetch(`${internalApiUrl}/api-internal/v1/stats/components`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await fetchComponents();
        setShowAddForm(false);
        setFormData({
          name: '',
          display_name: '',
          status: 'OPERATIONAL',
          region: '',
          environment: 'production',
        });
      }
    } catch (error) {
      console.error('Failed to create component', error);
    }
  }

  async function handleUpdate(id: string, data: Partial<Component>) {
    try {
      const response = await fetch(`${internalApiUrl}/api-internal/v1/stats/components/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });

      if (response.ok) {
        await fetchComponents();
        setEditingId(null);
      }
    } catch (error) {
      console.error('Failed to update component', error);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Are you sure you want to delete this component?')) return;

    try {
      const response = await fetch(`${internalApiUrl}/api-internal/v1/stats/components/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (response.ok) {
        await fetchComponents();
      }
    } catch (error) {
      console.error('Failed to delete component', error);
    }
  }

  const statusOptions = [
    { value: 'OPERATIONAL', label: 'Operational' },
    { value: 'DEGRADED_PERFORMANCE', label: 'Degraded Performance' },
    { value: 'PARTIAL_OUTAGE', label: 'Partial Outage' },
    { value: 'MAJOR_OUTAGE', label: 'Major Outage' },
    { value: 'MAINTENANCE', label: 'Maintenance' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="relative">
          <div className="w-10 h-10 border-2 border-gray-200 rounded-full" />
          <div className="absolute inset-0 w-10 h-10 border-2 border-transparent border-t-unlimit-lime rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-bold text-navy-900">Component Management</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-unlimit-lime text-navy-900 font-semibold text-sm rounded-lg hover:bg-unlimit-lime-dark transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Add Component</span>
        </button>
      </div>

      {showAddForm && (
        <div className="bg-white rounded-xl shadow-card border border-gray-100 p-5 mb-5 animate-slide-down">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-bold text-navy-900">Add New Component</h3>
            <button onClick={() => setShowAddForm(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Name (slug)</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 bg-unlimit-gray-light border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-unlimit-lime/50 focus:border-unlimit-lime outline-none transition-all"
                placeholder="api-gateway"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Display Name</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                className="w-full px-3 py-2 bg-unlimit-gray-light border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-unlimit-lime/50 focus:border-unlimit-lime outline-none transition-all"
                placeholder="API Gateway"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 bg-unlimit-gray-light border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-unlimit-lime/50 focus:border-unlimit-lime outline-none transition-all appearance-none"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Region</label>
              <input
                type="text"
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                className="w-full px-3 py-2 bg-unlimit-gray-light border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-unlimit-lime/50 focus:border-unlimit-lime outline-none transition-all"
                placeholder="us-east-1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Environment</label>
              <select
                value={formData.environment}
                onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                className="w-full px-3 py-2 bg-unlimit-gray-light border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-unlimit-lime/50 focus:border-unlimit-lime outline-none transition-all appearance-none"
              >
                <option value="production">Production</option>
                <option value="sandbox">Sandbox</option>
                <option value="staging">Staging</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              className="px-4 py-2 text-sm bg-navy-900 text-white font-semibold rounded-lg hover:bg-navy-800 transition-colors"
            >
              Add Component
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-card border border-gray-100 overflow-hidden">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Name</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Region</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Environment</th>
              <th className="px-5 py-3 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {components.map((component) => (
              <tr key={component.id} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-5 py-3 whitespace-nowrap">
                  <div className="text-sm font-semibold text-navy-900">{component.display_name}</div>
                  <div className="text-[11px] text-gray-400">{component.name}</div>
                </td>
                <td className="px-5 py-3 whitespace-nowrap">
                  {editingId === component.id ? (
                    <select
                      defaultValue={component.status}
                      onChange={(e) => handleUpdate(component.id, { status: e.target.value })}
                      className="px-2 py-1 border border-gray-200 rounded-md text-xs bg-unlimit-gray-light"
                    >
                      {statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`px-2 py-1 text-[11px] font-semibold rounded-md ${
                      component.status === 'OPERATIONAL' ? 'bg-emerald-50 text-emerald-700' :
                      component.status === 'DEGRADED_PERFORMANCE' ? 'bg-yellow-50 text-yellow-700' :
                      component.status === 'MAINTENANCE' ? 'bg-blue-50 text-blue-700' :
                      'bg-rose-50 text-rose-400'
                    }`}>
                      {component.status.replace(/_/g, ' ')}
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-400">
                  {component.region || '-'}
                </td>
                <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-400">
                  {component.environment || '-'}
                </td>
                <td className="px-5 py-3 whitespace-nowrap text-right">
                  <div className="flex justify-end space-x-1">
                    {editingId === component.id ? (
                      <button
                        onClick={() => setEditingId(null)}
                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors"
                      >
                        <Save className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => setEditingId(component.id)}
                        className="p-1.5 text-gray-400 hover:text-navy-900 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(component.id)}
                      className="p-1.5 text-gray-400 hover:text-rose-400 hover:bg-rose-50 rounded-md transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
